import { ViewController } from '@estee/elc-service';
import { action, observable, computed } from 'mobx';
import { PriceFormatter } from '@estee/elc-formatters';
import {
    IProductInstallmentsPrice,
    IProductInstallmentsConfig
} from '../views/product-installments-price/ProductInstallmentsPrice';
import { ComponentTypes } from '../constants/ComponentTypes';

export interface IInstallmentsControllerConfig {
    installments: IProductInstallmentsConfig;
}

export class ProductInstallmentsPriceViewController extends ViewController {
    @observable private isInstallmentsModalOpen: boolean = false;
    private priceFormatter: PriceFormatter;
    private componentType: ComponentTypes;
    private config: IInstallmentsControllerConfig;

    constructor(priceFormatter: PriceFormatter) {
        super();
        this.priceFormatter = priceFormatter;
    }

    @action
    public initialize = (props: IProductInstallmentsPrice) => {
        const { componentType, config } = props;
        this.componentType = componentType;
        this.config = config as IInstallmentsControllerConfig;
    };

    @computed
    public get componentConfig() {
        const { overrides, ...installmentsConfig } =
            this.config.installments || ({} as IProductInstallmentsConfig);

        const configOverrides = overrides && overrides[this.componentType];

        return { ...installmentsConfig, ...configOverrides };
    }

    @computed
    get data() {
        return {
            isInstallmentsModalOpen: this.isInstallmentsModalOpen,
            componentConfig: this.componentConfig
        };
    }

    public getFormattedPrice = (price: number) => {
        return this.priceFormatter.formatPriceWithCurrency(price);
    };

    public getIntervalPriceFormatted = (defaultPrice: number, installmentsNumber: number) => {
        const intervalPrice = this.setIntervalPrice(defaultPrice, installmentsNumber);

        return this.getFormattedPrice(intervalPrice);
    };

    private getPossibleInstallments = (minNumber: number, maxNumber: number) => {
        const possibleInstallments = [];

        for (let i = maxNumber; i >= minNumber; i--) {
            possibleInstallments.push(i);
        }

        return possibleInstallments;
    };

    public getComputedNumberOfInstallments = (price: number) => {
        const minNumber = this.componentConfig.minNumberOfInstallments;
        const maxNumber = this.componentConfig.maxNumberOfInstallments;
        const minAmount = this.componentConfig.installmentMinimumAmount;

        if (minNumber && maxNumber && minAmount) {
            const isInstallmentAvailable = price / minNumber >= minAmount;
            const possibleInstallments = this.getPossibleInstallments(minNumber, maxNumber);
            const numberOfInstallments = possibleInstallments.find(
                (installmentsNumber) => price / installmentsNumber >= minAmount
            );

            return isInstallmentAvailable && numberOfInstallments ? numberOfInstallments : 0;
        }

        return 0;
    };

    @action
    public openModal = () => {
        this.isInstallmentsModalOpen = true;
    };

    @action
    public closeModal = () => {
        this.isInstallmentsModalOpen = false;
    };

    public setIntervalPrice = (price: number, intervals: number) => {
        const absoluteIntervals = Math.abs(intervals);
        if (absoluteIntervals > 0) {
            return price / absoluteIntervals;
        }

        return price;
    };
}
